// Plain-English formatting for tennis picks. The engine emits machine codes
// (market="total_games", selection="over 32" / "home +7" / "player_a"); users
// must never see those. This turns them into natural language, resolving side
// tokens (home/away/player_a) to the actual player names from "A vs B".

// Human label for a market category (the drawer's market column).
const MARKET_LABEL: Record<string, string> = {
  match_winner: "Match winner",
  set_win: "To win a set",
  games_handicap: "Games handicap",
  total_games: "Total games",
  total_sets: "Total sets",
  first_set: "First set",
  correct_score: "Correct score",
  btts: "Both to score",
};

export function marketLabel(code: string): string {
  return MARKET_LABEL[code] ?? prettifyCode(code);
}

// Fallback: snake_case → Title Case, so an unmapped code is still readable.
function prettifyCode(code: string): string {
  return (code || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || "Market";
}

function players(matchName: string | null | undefined): { a: string; b: string } {
  const [a, b] = String(matchName || "").split(/\s+vs\.?\s+/i);
  return { a: (a || "").trim(), b: (b || "").trim() };
}

function sideName(tok: string, a: string, b: string): string | null {
  const t = tok.toLowerCase();
  if (t === "home" || t === "player_a" || t === "a") return a || "Player A";
  if (t === "away" || t === "player_b" || t === "b") return b || "Player B";
  return null;
}

// Humanise just the selection (e.g. "over 32" → "Over 32 games").
export function formatSelection(market: string, selection: string | null, matchName?: string | null): string {
  const s = String(selection ?? "").trim();
  if (!s) return "";
  const { a, b } = players(matchName);
  const lower = s.toLowerCase();

  let m: RegExpMatchArray | null;
  // Over/Under with a line: "over 32", "under 2.5"
  if ((m = lower.match(/^(over|under)\s+([\d.]+)$/))) {
    const dir = m[1] === "over" ? "Over" : "Under";
    const unit = market === "total_sets" ? "sets" : "games";
    return `${dir} ${m[2]} ${unit}`;
  }
  // Handicap: "home +7", "away +2.5", "player_a -4.5"
  if ((m = lower.match(/^(home|away|player_a|player_b)\s*([+-][\d.]+)$/))) {
    return `${sideName(m[1] ?? "", a, b)} ${m[2]} games`;
  }
  // Bare side token → player name
  if (["home", "away", "player_a", "player_b", "a", "b"].includes(lower)) {
    return sideName(lower, a, b) || s;
  }
  if (lower === "yes") return "Yes";
  if (lower === "no") return "No";
  // Already human (a player/team name, "Over 2.5 Goals", etc.) — keep as-is.
  return s;
}

// The full plain-English pick phrase shown on a row / badge.
export function formatPick(market: string, selection: string | null, matchName?: string | null): string {
  const sel = formatSelection(market, selection, matchName);
  switch (market) {
    case "match_winner": return sel ? `${sel} to win` : "Match winner";
    case "set_win": return sel ? `${sel} to win a set` : "To win a set";
    case "first_set": return sel ? `${sel} to win the first set` : "First set winner";
    case "games_handicap": return sel || "Games handicap";       // sel = "Name +7 games"
    case "total_games": return sel || "Total games";             // sel = "Over 32 games"
    case "total_sets": return sel || "Total sets";               // sel = "Over 2.5 sets"
    case "correct_score": return selection ? `Correct set score: ${selection}` : "Correct score";
    case "btts": return sel ? `Both players to win a game — ${sel}` : "Both to score";
    default: return sel || marketLabel(market);
  }
}
